<template>
  <div :class="['modal', { 'is-active': isOpen }]">
    <div class="modal-background" @click="closeUploadModal" />
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">Προσθήκη Φωτογραφιών</p>
        <button type="button" class="delete" @click="closeUploadModal" />
      </header>
      <section class="modal-card-body">
        <div v-if="hasPhotos" class="images">
          <ul class="images__list">
            <li v-for="photo in photos" :key="photo.id" class="images__item">
              <button
                type="button"
                class="images__btn"
                @click="handleSelect(photo)"
              >
                <photo :photo="photo" />
              </button>
            </li>
          </ul>
        </div>
        <div v-if="previews && previews.length > 0" class="has-text-centered">
          <div class="carousel-viewport">
            <div :style="trackStyle" class="carousel-track">
              <div
                v-for="(preview, i) in previews"
                :key="preview"
                class="carousel-slide"
              >
                <img :src="preview" :alt="preview" />
                <button
                  type="button"
                  class="button is-danger"
                  @click="handleRemoveImage(i)"
                >
                  <span class="icon"><i class="fa fa-trash"/></span>
                  <span>Αφαίρεση</span>
                </button>
              </div>
            </div>
          </div>

          <div class="is-flex carousel-controls mt-3">
            <button
              :disabled="index === 0"
              class="button"
              type="button"
              @click="prev"
            >
              Prev
            </button>

            <div class="buttons has-addons">
              <button
                v-for="(_, i) in previews"
                :key="i"
                :class="{ 'is-link': i === index }"
                class="button"
                type="button"
                @click="goTo(i)"
              >
                {{ i + 1 }}
              </button>
            </div>

            <button
              :disabled="index === previews.length - 1"
              class="button"
              type="button"
              @click="next"
            >
              Next
            </button>
          </div>
        </div>
        <div v-else>
          <div class="image-container">
            <button
              :class="{ 'is-loading': isSaving }"
              type="button"
              class="button is-primary"
            >
              <span class="icon is-small"><i class="fa fa-image"/></span>
              <span>{{ buttonText }}</span>
            </button>
            <input
              v-validate="`mimes:${rule}`"
              ref="file"
              :id="id"
              :disabled="isSaving"
              :accept="rule"
              :multiple="isMultiple"
              type="file"
              name="file"
              @change="onFileChange"
            />
            <label :for="id" />
          </div>
          <p v-show="errors.has('file')" class="help is-danger">
            Εισάγετε έγκυρη εικόνα (.jpeg, .jpg, .png, .webp, .gif) έως 5MB
          </p>
        </div>
      </section>
      <footer v-if="files" class="modal-card-foot">
        <div class="field is-grouped">
          <div class="control">
            <button
              :class="{ 'is-loading': isSaving }"
              :disabled="isSaving"
              type="button"
              class="button is-primary"
              @click="upload"
            >
              <span class="icon is-small"><i class="fa fa-save"/></span
              ><span>Προσθήκη</span>
            </button>
          </div>
          <div class="control">
            <button type="button" class="button" @click="reset">
              <span class="icon is-small"><i class="fa fa-undo"/></span
              ><span>Επαναφορά</span>
            </button>
          </div>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import Photo from '@/views/components/Photo';
import FormSubmit from '@/views/components/FormSubmit';

export default {
  components: {
    Photo,
    FormSubmit,
  },

  props: {
    id: {
      type: String,
      required: false,
      default: 'file',
    },
    photos: {
      type: Array,
      required: false,
      default() {
        return [];
      },
    },
    rule: {
      type: String,
      required: false,
      default() {
        return 'image/*';
      },
    },
    isOpen: Boolean,
    isSaving: Boolean,
    isMultiple: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      files: null,
      previews: null,
      index: 0,
    };
  },

  computed: {
    hasPhotos() {
      return this.photos.length > 0;
    },

    buttonText() {
      return 'Επιλέξτε μία ή περισσότερες φωτογραφίες (.jpeg, .jpg, .png, .webp, .gif) συνολικού μεγέθους έως 5MB';
    },

    trackStyle() {
      return {
        transform: `translateX(-${this.index * 100}%)`,
        transition: 'transform 300ms ease',
      };
    },
  },

  watch: {
    isOpen(newVal, oldVal) {
      if (newVal !== oldVal && newVal === false) {
        this.reset();
      }
    },
  },

  methods: {
    handleSelect(photo) {
      fetch(photo.url)
        .then(response => {
          if (!response.ok) {
            throw Error('Network response was not ok');
          }
          return response.blob();
        })
        .then(blob => {
          this.files = [
            new File([blob], 'image', {
              type: blob.type,
            }),
          ];

          this.createPreview(this.files);
        })
        .catch(err => console.error(err));
    },

    handleRemoveImage(i) {
      this.files.splice(i, 1);
      this.previews.splice(i, 1);
      if (this.index >= this.previews.length) {
        this.index = this.previews.length - 1;
      }
      if (this.previews.length === 0) {
        this.reset();
      }
    },

    onFileChange(e) {
      const files = Array.from(e.target.files || e.dataTransfer.files);
      if (!files.length) {
        return;
      }

      this.files = files;

      this.createPreviews(this.files);
    },

    createPreviews(files) {
      this.$validator
        .validateAll()
        .then(() => {
          this.previews = files.map(file => URL.createObjectURL(file));
        })
        .catch(err => console.log(err));
    },

    upload() {
      this.$emit('upload', this.isMultiple ? this.files : this.files[0]);
    },

    closeUploadModal() {
      this.$emit('closeUploadModal');

      this.reset();
    },

    reset() {
      this.errors.clear();
      this.files = null;
      this.previews = null;
      this.index = 0;
    },

    goTo(i) {
      const max = this.previews.length - 1;
      this.index = Math.max(0, Math.min(i, max));
    },
    prev() {
      this.goTo(this.index - 1);
    },
    next() {
      this.goTo(this.index + 1);
    },
  },
};
</script>

<style lang="scss" type="text/scss" scoped>
img {
  max-width: 100%;
}

.image-container {
  background-color: #fafafa;
  height: 200px;
  position: relative;
  padding: 40px 0 0;
  border: 2px dashed #dddddd;

  .button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  input {
    opacity: 0;
    border: 1px solid red;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  label {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    &:hover {
      cursor: pointer;
    }
  }
}
.images {
  &__list {
    align-items: center;
    display: flex;
    justify-content: flex-start;
    height: 84px;
    margin-bottom: 20px;
    overflow: auto;
  }

  &__item {
    flex: 0 0 auto;
    padding-left: 5px;
    padding-right: 5px;
  }

  &__btn {
    background-color: #fff;
    border: none;
    border-radius: 4px;
    box-shadow: 0 2px 3px rgba(0, 0, 0, 0.16);
    cursor: pointer;
    height: 62px;
    padding: 5px;
    width: 62px;
  }
}
.carousel-viewport {
  overflow: hidden;
  margin-bottom: 20px;
}
.carousel-track {
  display: flex;
  transition: transform 300ms ease;
  will-change: transform;
}
.carousel-slide {
  min-width: 100%;
  flex: 0 0 100%;
  position: relative;
}
.carousel-controls {
  justify-content: center;
  align-items: center;
  margin: auto;
}
.carousel-controls .buttons,
.carousel-controls button {
  margin-bottom: 0;
}
.carousel-slide img {
  height: 400px;
  object-fit: contain;
  background: white;
}
.carousel-slide button {
  position: absolute;
  top: 10px;
  right: 10px;
}
</style>
